/**
 * Keeps the bag's figures true when an app changes the bag after the theme.
 *
 * Discount Ninja applies its offers through a private cart attribute that
 * it writes with /cart/update.js just after every change to the bag, and
 * only then does Shopify price the discount. Anything drawn from the change
 * itself shows the bag before the offer: with 20% off two activewear
 * pieces, the Added to bag panel showed both at full price.
 *
 * Every change to the bag, from anyone, is seen here as it finishes,
 * through the browser's resource timing, so nothing wraps fetch. Once the
 * changes settle, the bag is read again, and any part of the page whose
 * figure no longer matches is drawn again: the header bag, the Added to bag
 * panel (data-nyl-cart-total on its subtotal) and Horizon's cart
 * (data-nyl-cart-total on cart-items-component). Redrawing only reads the
 * bag, so it never sets this off again.
 */
(() => {
  if (!('PerformanceObserver' in window)) return;

  const CHANGE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  // Discount Ninja writes within a moment of the change it follows.
  const SETTLE = 300;

  const root = () => window.Shopify?.routes?.root || '/';
  const dispatch = (name) => document.dispatchEvent(new CustomEvent(name, { bubbles: true }));

  let timer = 0;

  const check = async () => {
    let cart;
    try {
      const response = await fetch(`${root()}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      return;
    }

    // True when anything the selector finds shows a figure other than this.
    const stale = (selector, read, figure) =>
      [...document.querySelectorAll(selector)].some((el) => read(el) !== figure);
    const shownTotal = (el) => el.dataset.nylCartTotal;
    const total = String(cart.total_price);

    if (stale('[data-nyl-cart-count]', (el) => el.textContent.trim(), String(cart.item_count))) {
      dispatch('cart:refresh');
    }
    if (stale('[data-nyl-qa-subtotal][data-nyl-cart-total]', shownTotal, total)) dispatch('nyl:bag:refresh');
    if (stale('cart-items-component[data-nyl-cart-total]', shownTotal, total)) dispatch('nyl:cart:changed');
  };

  const changedTheBag = (entry) => {
    try {
      return CHANGE.test(new URL(entry.name, window.location.href).pathname);
    } catch (error) {
      return false;
    }
  };

  try {
    new PerformanceObserver((list) => {
      if (!list.getEntries().some(changedTheBag)) return;
      clearTimeout(timer);
      timer = setTimeout(check, SETTLE);
    }).observe({ type: 'resource', buffered: true });
  } catch (error) {
    // Without resource timing the page works as before: the figures catch
    // up on the next change the theme makes itself or the next page load.
  }
})();
